import AppConfirmationModal from '@nxsys/components/AppConfirmationModal';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useRef, useState } from 'react';
import WorkingPatternForm, {
  WorkingPatternFormRef,
  WorkingPatternValues,
} from './WorkingPatternForm';
import { WORKING_PATTERN_OPTIONS } from './workingPatternOptions';
import { IWorkingPattern } from '@nxsys/types/models/workingPattern';
import { DayOfWeekEnum } from '@nxsys/constants/AppEnums';

type EditWorkingPatternModal = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (params: WorkingPatternValues) => Promise<void>;
  data: IWorkingPattern;
};

const mapWorkingPatternToOptions = (
  workingPattern: IWorkingPattern['workingPattern'],
) => {
  const result = [];
  if (workingPattern.monday === 'Y') {
    result.push(
      WORKING_PATTERN_OPTIONS.find(
        (item) => item.value === DayOfWeekEnum.MONDAY,
      ),
    );
  }
  if (workingPattern.tuesday === 'Y') {
    result.push(
      WORKING_PATTERN_OPTIONS.find(
        (item) => item.value === DayOfWeekEnum.TUESDAY,
      ),
    );
  }
  if (workingPattern.wednesday === 'Y') {
    result.push(
      WORKING_PATTERN_OPTIONS.find(
        (item) => item.value === DayOfWeekEnum.WEDNESDAY,
      ),
    );
  }
  if (workingPattern.thursday === 'Y') {
    result.push(
      WORKING_PATTERN_OPTIONS.find(
        (item) => item.value === DayOfWeekEnum.THURSDAY,
      ),
    );
  }
  if (workingPattern.friday === 'Y') {
    result.push(
      WORKING_PATTERN_OPTIONS.find(
        (item) => item.value === DayOfWeekEnum.FRIDAY,
      ),
    );
  }
  if (workingPattern.saturday === 'Y') {
    result.push(
      WORKING_PATTERN_OPTIONS.find(
        (item) => item.value === DayOfWeekEnum.SATURDAY,
      ),
    );
  }
  if (workingPattern.sunday === 'Y') {
    result.push(
      WORKING_PATTERN_OPTIONS.find(
        (item) => item.value === DayOfWeekEnum.SUNDAY,
      ),
    );
  }
  return result;
};

const EditWorkingPatternModal: React.FC<EditWorkingPatternModal> = ({
  isOpen,
  toggle,
  onSubmit,
  data,
}: EditWorkingPatternModal) => {
  const { messages } = useTypedIntl();
  const formRef = useRef<WorkingPatternFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  // NX-4948: like the Old UI, setting a pattern as the company default asks
  // first — every employee without their own pattern moves to it.
  const [pendingDefault, setPendingDefault] =
    useState<WorkingPatternValues | null>(null);

  const save = async (values: WorkingPatternValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
      formRef.current?.reset();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (values: WorkingPatternValues) => {
    if (values.isDefault && !data?.isDefault) {
      setPendingDefault(values);
      return;
    }
    await save(values);
  };

  return (
    <AppModalContainer
      title={messages['common.editWorkingPattern']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
      minHeight="200px"
    >
      <WorkingPatternForm
        formRef={formRef}
        onSubmit={handleSubmit}
        defaultValues={{
          name: data?.name || '',
          isDefault: data?.isDefault || false,
          workingDaysFTE: data?.workingDaysFTE?.toString() || '',
          workingDays: data?.workingDays?.toString() || '',
          workingHoursFTE: data?.workingHoursFTE?.toString() || '',
          workingHours: data?.workingHours?.toString() || '',
          workingPattern: mapWorkingPatternToOptions(
            data?.workingPattern,
          ) as any,
        }}
      />
      <AppConfirmationModal
        open={!!pendingDefault}
        icon={null}
        title={messages['common.setAsWorkingPatternDefault']}
        paragraph={`Are you sure you want to set the working pattern "${pendingDefault?.name ?? ''}" as default?`}
        loading={isSubmitting}
        onDeny={() => setPendingDefault(null)}
        onConfirm={async () => {
          if (!pendingDefault) return;
          await save(pendingDefault);
          setPendingDefault(null);
        }}
      />
    </AppModalContainer>
  );
};

export default EditWorkingPatternModal;
